import api from '../../../services/Api'
import {saveAs} from 'file-saver'
import {
  CategoriaLancamentoQualidade,
  IAuditoriaQualidadeHistoricosResultado,
  ILancamentosQualidadePagina,
} from '../interfaces/IAuditoriaQualidadeHistoricos'

const resultadoVazio: IAuditoriaQualidadeHistoricosResultado = {
  impactoFiscalTotal: 0,
  totalErros: 0,
  totalAlertas: 0,
  totalAvisos: 0,
  totalPendentes: 0,
  totalReferencialDivergente: 0,
  totalLinhasAnalisadas: 0,
  totalLinhasComErro: 0,
  totalLinhasQualificadas: 0,
  totalLinhasSemMulta: 0,
  status: 'CONCILIADO',
  mensagemDirecionamento: '',
  fundamentacaoLegal: '',
  contas: [],
}

// DEVN3-593: empresas com muitos lançamentos (ex.: 1,4M linhas de Fornecedores/Clientes) não são
// calculadas na requisição — a API enfileira o processamento e responde 202 até o resultado ficar pronto.
export const INTERVALO_POLLING_QUALIDADE_MS = 10_000
export const TENTATIVAS_MAXIMAS_POLLING_QUALIDADE = 180 // ~30 min

interface ParamsResultado {
  empresaId: number
  periodo?: string
}

interface OpcoesResultado {
  // false (aba TODOS): não bloqueia esperando o processamento — devolve um resultado marcado
  // como `processando` e quem chamou decide se acompanha em segundo plano.
  aguardar?: boolean
  // Chamado a cada tentativa em que a API ainda respondeu 202.
  aoAguardar?: (tentativa: number) => void
  intervaloMs?: number
  tentativasMaximas?: number
}

const esperar = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const resultadoProcessando = (): IAuditoriaQualidadeHistoricosResultado => ({
  ...resultadoVazio,
  status: 'AVISO',
  mensagemDirecionamento:
    'Auditoria em processamento: esta empresa/período tem um grande volume de lançamentos e o resultado ainda está sendo calculado.',
  processando: true,
})

export const AuditoriaQualidadeHistoricosService = {
  async buscarResultado(
    params: ParamsResultado,
    opcoes: OpcoesResultado = {}
  ): Promise<IAuditoriaQualidadeHistoricosResultado> {
    const {aguardar = true, aoAguardar, intervaloMs = INTERVALO_POLLING_QUALIDADE_MS} = opcoes
    const tentativasMaximas = opcoes.tentativasMaximas ?? TENTATIVAS_MAXIMAS_POLLING_QUALIDADE

    for (let tentativa = 1; ; tentativa++) {
      const response = await api.get<IAuditoriaQualidadeHistoricosResultado>(
        'auditoria-qualidade-historicos/resultado',
        {
          params: {
            empresaIds: params.empresaId,
            periodo: params.periodo || '',
          },
        }
      )

      if (response.status !== 202) return response.data || resultadoVazio

      if (!aguardar) return resultadoProcessando()

      if (tentativa >= tentativasMaximas)
        throw new Error(
          'A auditoria de qualidade de históricos ainda está em processamento. Tente novamente em alguns minutos.'
        )

      aoAguardar?.(tentativa)
      await esperar(intervaloMs)
    }
  },

  // Página do drill-down (linhas desqualificadas ou pendentes de um referencial de uma esteira).
  async buscarLancamentos(params: {
    esteiraId: number
    referencial: string
    categoria: CategoriaLancamentoQualidade
    pagina: number
    tamanhoPagina: number
  }): Promise<ILancamentosQualidadePagina> {
    const response = await api.get<ILancamentosQualidadePagina>(
      'auditoria-qualidade-historicos/lancamentos',
      {
        params: {
          esteiraId: params.esteiraId,
          referencial: params.referencial,
          categoria: params.categoria,
          page: params.pagina,
          pageSize: params.tamanhoPagina,
        },
      }
    )
    return response.data
  },

  // CSV completo (todas as linhas desqualificadas e pendentes da esteira), gerado no servidor em
  // streaming — o navegador não monta centenas de milhares de linhas.
  async exportarCsv(params: {
    esteiraId: number
    referencial?: string
    categoria?: CategoriaLancamentoQualidade
  }): Promise<void> {
    const response = await api.get('auditoria-qualidade-historicos/lancamentos/exportar', {
      params,
      responseType: 'blob',
      timeout: 0, // sem timeout do axios: o servidor faz streaming e pode levar minutos
    })

    saveAs(
      new Blob([response.data], {type: 'text/csv;charset=utf-8;'}),
      `qualidade_historicos_esteira_${params.esteiraId}.csv`
    )
  },
}
